import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BundleFloorLevel } from '../../schemas/apiResponse.schemas';
import { fetchBundleFloorLevels } from '../../services/bundleFloorAPI';
import { fetchOrderShelfLocations } from '../../services/skuShelfIndexAPI';
import type { OrderShelfLocationMap } from '../../types/apiTypes';
import { getApiErrorMessage } from '../../services/errorUtils';
import { normalizeSku } from '../../utils/normalizeSku';
import { stripByKink } from '@/shared/utils/stripByKink';
import { renderSkuWithSoftBreaks } from '../../utils/skuSoftBreak';
import ProductImage from '../shared/ProductImage';
import ProductInfoModal from '../shared/ProductInfoModal';
import { NavbarLeftPageActions, NavbarPageActions } from '../layout/NavbarPageActions';
import BundleWorkStatBox from './BundleWorkStatBox';
import { compareShelfIndices } from './orderdetail/shelfUtils';
import { shouldSurfaceBundleWorkRow } from './orderdetail/bundleWorkOrderLogic';
import './OrdersList.css';
import './SingleSkuOrdersList.css';
import './BundleWorkOrdersList.css';

const BUNDLE_WORK_SORT = {
  Alphabetical: 'alphabetical',
  ShelfIndex: 'shelf-index',
} as const;

type BundleWorkSort = (typeof BUNDLE_WORK_SORT)[keyof typeof BUNDLE_WORK_SORT];

interface BundleWorkRow {
  readonly sku: string;
  readonly itemName: string | null;
  readonly currentQty: number;
  readonly baselineQty: number;
  readonly shelfIndex: string | null;
}

function rowsForBundleListMode(
  items: readonly BundleWorkRow[],
  floorBySku: Readonly<Record<string, BundleFloorLevel>>,
  showLowBundles: boolean,
): readonly BundleWorkRow[] {
  if (!showLowBundles) {
    return items;
  }
  return items.filter((row) => {
    const floor = floorBySku[row.sku];
    return floor !== undefined && shouldSurfaceBundleWorkRow(floor);
  });
}

function sortBundleWorkRows(
  items: readonly BundleWorkRow[],
  sortMode: BundleWorkSort,
): BundleWorkRow[] {
  return [...items].sort((left, right) => {
    if (sortMode === BUNDLE_WORK_SORT.ShelfIndex) {
      const shelfCmp = compareShelfIndices(left.shelfIndex, right.shelfIndex);
      if (shelfCmp !== 0) {
        return shelfCmp;
      }
    }
    return left.sku.localeCompare(right.sku, undefined, { sensitivity: 'base' });
  });
}

function filterBundleWorkRows(
  items: readonly BundleWorkRow[],
  filterText: string,
): readonly BundleWorkRow[] {
  if (!filterText.trim()) {
    return items;
  }
  const searchTerm = filterText.toUpperCase();
  return items.filter((row) => {
    const sku = row.sku.toUpperCase();
    const name = row.itemName?.toUpperCase() ?? '';
    return sku.includes(searchTerm) || name.includes(searchTerm);
  });
}

function BundleWorkOrdersList(): React.ReactElement {
  const navigate = useNavigate();
  const [rows, setRows] = useState<readonly BundleWorkRow[]>([]);
  const [floorBySku, setFloorBySku] = useState<Readonly<Record<string, BundleFloorLevel>>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [showLowBundles, setShowLowBundles] = useState(true);
  const [sortMode, setSortMode] = useState<BundleWorkSort>(BUNDLE_WORK_SORT.Alphabetical);
  const [productInfoItem, setProductInfoItem] = useState<{
    sku: string;
    itemName?: string;
    name?: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setLoadError(null);
      try {
        const floorLevels = await fetchBundleFloorLevels();
        if (cancelled) return;

        const skus = floorLevels.map((entry) => entry.sku);
        const shelfMap: OrderShelfLocationMap = skus.length > 0
          ? await fetchOrderShelfLocations(skus).catch((): OrderShelfLocationMap => ({}))
          : {};
        if (cancelled) return;

        const nextFloorBySku: Record<string, BundleFloorLevel> = {};
        floorLevels.forEach((entry) => {
          nextFloorBySku[entry.sku] = entry;
        });
        setFloorBySku(nextFloorBySku);

        setRows(
          floorLevels.map((entry) => {
            const normalized = normalizeSku(entry.sku);
            const loc = shelfMap[entry.sku] ?? shelfMap[normalized];
            const itemNameRaw = entry.itemName?.trim() || null;
            const itemName = itemNameRaw ? stripByKink(itemNameRaw) || itemNameRaw : null;
            return {
              sku: entry.sku,
              itemName,
              currentQty: entry.quantityOnFloor,
              baselineQty: entry.targetQty,
              shelfIndex: loc?.shelfIndex ?? null,
            };
          }),
        );
      } catch (err: unknown) {
        if (cancelled) return;
        setRows([]);
        setFloorBySku({});
        setLoadError(getApiErrorMessage(err, 'Failed to load bundle work orders'));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const modeRows = useMemo(
    () => rowsForBundleListMode(rows, floorBySku, showLowBundles),
    [rows, floorBySku, showLowBundles],
  );

  const filteredRows = useMemo(
    () => sortBundleWorkRows(filterBundleWorkRows(modeRows, filterText), sortMode),
    [modeRows, filterText, sortMode],
  );

  const openWorkOrder = (sku: string): void => {
    navigate(`/bundle-work-order/${encodeURIComponent(sku)}`, {
      state: {
        fromPath: '/bundle-work-orders',
        siblingOrderIds: filteredRows.map((row) => row.sku),
        floorEntry: floorBySku[sku],
      },
    });
  };

  return (
    <div className="orders-container single-sku-page bundle-work-page">
      <NavbarLeftPageActions>
        <button
          type="button"
          className={`orders-hide-issues-btn bwo-sort-btn${
            sortMode === BUNDLE_WORK_SORT.Alphabetical ? ' bwo-sort-btn-active' : ' bwo-sort-btn-inactive'
          }`}
          aria-pressed={sortMode === BUNDLE_WORK_SORT.Alphabetical}
          onClick={() => setSortMode(BUNDLE_WORK_SORT.Alphabetical)}
        >
          Sort alphabetically
        </button>
        <button
          type="button"
          className={`orders-hide-issues-btn bwo-sort-btn${
            sortMode === BUNDLE_WORK_SORT.ShelfIndex ? ' bwo-sort-btn-active' : ' bwo-sort-btn-inactive'
          }`}
          aria-pressed={sortMode === BUNDLE_WORK_SORT.ShelfIndex}
          onClick={() => setSortMode(BUNDLE_WORK_SORT.ShelfIndex)}
        >
          Sort by index
        </button>
      </NavbarLeftPageActions>
      <NavbarPageActions>
        <button
          type="button"
          className="orders-hide-issues-btn"
          aria-pressed={!showLowBundles}
          onClick={() => setShowLowBundles((current) => !current)}
        >
          {showLowBundles ? 'Show all bundles' : 'Show low bundles'}
        </button>
      </NavbarPageActions>
      {loadError ? <p className="single-sku-error">{loadError}</p> : null}
      {!loading && modeRows.length === 0 ? (
        <p className="bwo-empty">No Bundle Work Orders</p>
      ) : null}
      {!loading && modeRows.length > 0 ? (
        <>
          <div className="bwo-filter-container">
            <input
              type="text"
              className="bwo-filter-input"
              placeholder="Filter by SKU or name..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              aria-label="Filter by SKU or name"
            />
            {filterText ? (
              <button
                type="button"
                className="bwo-filter-clear"
                onClick={() => setFilterText('')}
                title="Clear filter"
              >
                ×
              </button>
            ) : null}
          </div>
          <div className="single-sku-body">
            {filteredRows.length === 0 ? (
              <p className="bwo-empty">No items match "{filterText}"</p>
            ) : (
              <ul className="single-sku-groups">
                {filteredRows.map((row) => (
                  <li key={row.sku} className="single-sku-group-item">
                    <div className="order-box-container single-sku-row bwo-row">
                      <button
                        type="button"
                        className="single-sku-thumb bwo-thumb"
                        onClick={() => {
                          setProductInfoItem({
                            sku: row.sku,
                            itemName: row.itemName || undefined,
                            name: row.itemName || undefined,
                          });
                        }}
                        aria-label={`Product details for ${row.sku}`}
                      >
                        <ProductImage sku={row.sku} alt={row.itemName || row.sku} />
                      </button>
                      <button
                        type="button"
                        className="single-sku-row-main bwo-row-main"
                        onClick={() => openWorkOrder(row.sku)}
                      >
                        <div className="bwo-sku-block">
                          <div className="single-sku-sku">{renderSkuWithSoftBreaks(row.sku)}</div>
                          {row.itemName ? (
                            <div className="single-sku-name">{row.itemName}</div>
                          ) : null}
                        </div>
                        <div className="bwo-metrics">
                          <BundleWorkStatBox label="Current" value={row.currentQty} />
                          <BundleWorkStatBox label="Baseline" value={row.baselineQty} />
                          <BundleWorkStatBox label="Shelf" value={row.shelfIndex ?? 'N/A'} />
                        </div>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
      {productInfoItem ? (
        <ProductInfoModal
          item={productInfoItem}
          onClose={() => setProductInfoItem(null)}
        />
      ) : null}
    </div>
  );
}

export default BundleWorkOrdersList;
